import classNames from 'classnames'
import * as React from 'react'
import { Bar as RechartsBar, Rectangle } from 'recharts'
import { BarRectangleItem } from 'recharts/types/cartesian/Bar'

enum BarColor {
  neutral,
  red,
  green,
}

type BarColorType = keyof typeof BarColor

interface ColorProps<ValueType> {
  color?: BarColorType | ((value: ValueType | null) => BarColorType)
}

type BarInnerProps<ValueType> = ColorProps<ValueType> & Omit<BarRectangleItem, 'color'>

export function BarInner<ValueType>({ color = 'neutral', ...props }: BarInnerProps<ValueType>) {
  const colorResult = typeof color === 'function' ? color(props.value as ValueType) : color
  const barColor = BarColor[colorResult as BarColorType] ?? BarColor.neutral

  return (
    <Rectangle
      {...props}
      className={classNames(props.className, {
        'fill-blue-400': barColor === BarColor.neutral,
        'fill-red-400': barColor === BarColor.red,
        'fill-green-400': barColor === BarColor.green,
      })}
    />
  )
}

type BarProps<ValueType> = ColorProps<ValueType> &
  React.PropsWithoutRef<Omit<React.ComponentProps<typeof RechartsBar>, 'color'>>

export class Bar<ValueType extends number | number[]> extends React.Component<BarProps<ValueType>> {
  static getComposedData = RechartsBar.getComposedData
  static displayName = RechartsBar.displayName

  static defaultProps = {
    ...RechartsBar.defaultProps,
    radius: [2, 2, 0, 0],
    activeBar: false,
    isAnimationActive: false,
  }

  render() {
    const { color, ...props } = this.props

    return <RechartsBar shape={<BarInner color={color} />} {...props} />
  }
}
